import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "./ui/dialog"
import { Button } from "./ui/button"
import { Alert, AlertDescription } from "./ui/alert"
import folderService from '../services/folderService';
import { Loader2, Folder, FolderOpen, ArrowRight, Check } from 'lucide-react';
import { cn } from "../lib/utils"
// import RadioGroup but we can implement custom selection easily

const MoveFileDialog = ({
    open,
    onClose,
    onMove,
    file,
    currentFolderId,
    loading = false
}) => {
    const [folders, setFolders] = useState([]);
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [loadingFolders, setLoadingFolders] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            loadFolders();
            setSelectedFolderId(currentFolderId);
        }
    }, [open, currentFolderId]);

    const loadFolders = async () => {
        try {
            setLoadingFolders(true);
            setError('');
            const response = await folderService.getAllFolders();
            const allFolders = response.folders || [];

            // Build hierarchy locally for display logic
            // But we can just use flat list with levels for simple rendering
            const folderMap = new Map();
            allFolders.forEach(folder => {
                folderMap.set(folder.id, { ...folder, children: [], level: 0 });
            });

            const rootFolders = [];
            allFolders.forEach(folder => {
                const folderNode = folderMap.get(folder.id);
                if (folder.parent_id === null) {
                    rootFolders.push(folderNode);
                } else {
                    const parent = folderMap.get(folder.parent_id);
                    if (parent) {
                        folderNode.level = parent.level + 1;
                        parent.children.push(folderNode);
                    } else {
                        rootFolders.push(folderNode);
                    }
                }
            });

            const flattenFolders = (folders) => {
                let result = [];
                folders.forEach(folder => {
                    result.push(folder);
                    if (folder.children.length > 0) {
                        result = result.concat(flattenFolders(folder.children));
                    }
                });
                return result;
            };

            const hierarchicalFolders = flattenFolders(rootFolders);
            setFolders(hierarchicalFolders);
        } catch (err) {
            console.error('Error loading folders:', err);
            setError('Gagal memuat daftar folder');
        } finally {
            setLoadingFolders(false);
        }
    };

    const handleMove = () => {
        if (selectedFolderId === currentFolderId) {
            setError('File sudah berada di folder ini');
            return;
        }
        onMove(selectedFolderId);
    };

    const handleClose = () => {
        if (!loading) {
            setSelectedFolderId(null);
            setError('');
            onClose();
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md h-[80vh] flex flex-col p-0 gap-0">
                <DialogHeader className="p-6 pb-2">
                    <DialogTitle className="flex items-center gap-2">
                        <ArrowRight className="h-5 w-5 text-primary" />
                        Pindahkan File: <span className="text-muted-foreground font-normal text-sm ml-1 truncate max-w-[200px]">{file?.original_name}</span>
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-2">
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {loadingFolders ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : (
                        <div className="space-y-1">
                            <div
                                className={cn(
                                    "flex items-center p-3 rounded-lg cursor-pointer transition-colors border",
                                    selectedFolderId === null
                                        ? "bg-primary/10 border-primary text-primary"
                                        : "hover:bg-gray-100 border-transparent text-gray-700"
                                )}
                                onClick={() => setSelectedFolderId(null)}
                            >
                                <FolderOpen className={cn("h-5 w-5 mr-3", selectedFolderId === null ? "text-primary" : "text-gray-400")} />
                                <span className="font-medium">My Drive (Root)</span>
                                {selectedFolderId === null && <Check className="ml-auto h-4 w-4" />}
                            </div>

                            {folders.map((folder) => (
                                <div
                                    key={folder.id}
                                    className={cn(
                                        "flex items-center p-3 rounded-lg cursor-pointer transition-colors border",
                                        selectedFolderId === folder.id
                                            ? "bg-primary/10 border-primary text-primary"
                                            : "hover:bg-gray-100 border-transparent text-gray-700",
                                        folder.id === currentFolderId && "opacity-50 cursor-not-allowed bg-gray-50"
                                    )}
                                    style={{ marginLeft: `${folder.level * 1.5}rem` }}
                                    onClick={() => {
                                        if (folder.id !== currentFolderId) {
                                            setSelectedFolderId(folder.id);
                                        }
                                    }}
                                >
                                    <Folder className={cn("h-5 w-5 mr-3 text-yellow-500")} />
                                    <span className="font-medium truncate">{folder.name}</span>
                                    {selectedFolderId === folder.id && <Check className="ml-auto h-4 w-4" />}
                                    {folder.id === currentFolderId && <span className="ml-auto text-xs text-muted-foreground">(Saat ini)</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter className="p-6 pt-2 border-t mt-auto gap-2 sm:gap-0 bg-gray-50">
                    <Button variant="ghost" onClick={handleClose} disabled={loading}>
                        Batal
                    </Button>
                    <Button
                        onClick={handleMove}
                        disabled={loading || loadingFolders || selectedFolderId === currentFolderId}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Memindahkan...
                            </>
                        ) : (
                            'Pindahkan Disini'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default MoveFileDialog;
